import classNames from 'classnames'
import { ReactNode, useMemo, useState } from 'react'
import { useFocused, useSelected } from 'slate-react'

import { Button } from 'core-components/library/button'

import styles from './TopPanelButtonsWrapper.module.scss'

export interface ITopPanelButtonsWrapperProps {
  /**
   * Width of the wrapper
   */
  width?: string
  /**
   * Callback function fires when delete button is clicked
   */
  onDelete?: () => Promise<void> | void
  /**
   * Callback function fires when setting button is clicked
   */
  onOpenSettings?: () => void
  /**
   * Callback function fires when save button is clicked
   */
  onSave?: () => Promise<void> | void
  /**
   * Callback function fires when open button is clicked
   */
  onOpenView?: () => void
  /**
   * Checking the editor status
   */
  disabled?: boolean
  /**
   * Defines the condition under which the action buttons should be displayed
   */
  visibleMode?: 'hover' | 'always'
  /**
   * Content for the wrapper
   */
  children: ReactNode
}

export const TopPanelButtonsWrapper = ({
  width,
  onDelete,
  onOpenSettings,
  onSave,
  onOpenView,
  disabled,
  visibleMode = 'hover',
  children,
}: ITopPanelButtonsWrapperProps) => {
  const selected = useSelected()
  const focused = useFocused()
  const isActive = selected && focused

  const [savingInProgress, setSavingInProgress] = useState(false)
  const [removingInProgress, setRemovingInProgress] = useState(false)

  const isActionsEmpty = useMemo(() => {
    return !onDelete && !onOpenSettings && !onOpenView && !onSave
  }, [onDelete, onOpenSettings, onOpenView, onSave])

  return (
    <div
      style={{ width }}
      className={classNames(styles.root, styles[`visibleMode--${visibleMode}`])}
    >
      <div contentEditable={false} className={styles.topPanel}>
        {!isActionsEmpty ? (
          <div className={styles.topPanelContent}>
            {onOpenView ? (
              <Button
                onClick={(e) => {
                  if (disabled) {
                    e.preventDefault()
                  }
                  onOpenView()
                }}
                className={styles.button}
                iconButton={'kbase kbase-full-screen kbase-sm'}
                variant={'text'}
              />
            ) : null}
            {onSave ? (
              <Button
                onClick={async () => {
                  setSavingInProgress(true)
                  await onSave()
                  setSavingInProgress(false)
                }}
                loading={savingInProgress}
                className={styles.button}
                iconButton={'kbase kbase-download kbase-sm'}
                variant={'text'}
              />
            ) : null}
            {onOpenSettings ? (
              <Button
                onClick={onOpenSettings}
                className={styles.button}
                iconButton={'kbase kbase-settings'}
                variant={'text'}
              />
            ) : null}
            {onDelete ? (
              <Button
                onClick={async () => {
                  setRemovingInProgress(true)
                  await onDelete()
                  setRemovingInProgress(false)
                }}
                loading={removingInProgress}
                className={styles.button}
                iconButton={'kbase kbase-delete kbase-sm'}
                variant={'text'}
              />
            ) : null}
          </div>
        ) : null}
      </div>
      <div className={classNames(styles.childrenWrapper, isActive ? styles.boxShadow : '')}>
        {children}
      </div>
    </div>
  )
}
